import { useEffect, useState } from "react";
import { Icon } from "../components/icons";
import { DataTable, EmptyAction, Notice, PageHeader, SlideForm } from "../components/ui";
import { supabase } from "../lib/supabase";

const blank = { name: "", slug: "", support_email: "", admin_email: "", admin_name: "" };
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-brand-ink transition duration-200 focus:border-brand-blue";

const statusTone = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  suspended: "border-rose-200 bg-rose-50 text-rose-800",
};

async function inviteFailureReason(error) {
  const raw = String(error?.message || "");
  if (error?.name === "FunctionsFetchError" || /failed to send a request/i.test(raw)) {
    return "The invitation could not be sent. Try again in a moment.";
  }
  let detail = "";
  try {
    const body = typeof error?.context?.json === "function" ? await error.context.json() : null;
    detail = body?.error || body?.message || "";
  } catch { /* the response body is not available */ }
  if (/smtp|error sending/i.test(detail)) {
    return "The invitation email could not be sent. Auth email still needs to be configured.";
  }
  if (detail && !/edge function|non-2xx/i.test(detail)) return detail;
  return "The administrator was not invited. Try again.";
}

function toSlug(value) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function tenantLoginUrl() {
  const host = window.location.hostname;
  if (host === "localhost") return "http://localhost:3000/login";
  if (host === "127.0.0.1") return "http://127.0.0.1:3000/login";
  return "https://app.globovms.africa/login";
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between gap-3 text-sm font-semibold text-slate-700">
        {label}
        {hint ? <span className="text-xs font-medium text-slate-400">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

export function Clients() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(blank);
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusId, setStatusId] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });

  async function load() {
    const { data, error: rpcError } = await supabase.rpc("platform_list_organizations");
    if (rpcError) {
      setListError(rpcError.message);
      setError(rpcError.message);
      setRows([]);
      return;
    }
    setListError("");
    setRows(data || []);
  }
  useEffect(() => { load(); }, []);

  function updateName(value) {
    setForm((current) => ({ ...current, name: value, slug: slugEdited ? current.slug : toSlug(value) }));
  }

  async function createClient(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!slugPattern.test(form.slug)) {
      setError("Slug can use lowercase letters, numbers, and single hyphens.");
      return;
    }
    const adminEmail = form.admin_email;
    setBusy(true);
    const { data: orgId, error: createError } = await supabase.rpc("platform_create_organization", {
      p_name: form.name, p_slug: form.slug, p_support_email: form.support_email || null,
    });
    if (createError) { setBusy(false); setError(createError.message); return; }
    const invite = await supabase.functions.invoke("platform-invite-admin", {
      body: { organization_id: orgId, email: adminEmail, full_name: form.admin_name, redirect_to: tenantLoginUrl() },
    });
    setBusy(false);
    setForm(blank);
    setSlugEdited(false);
    setOpen(false);
    if (invite.error || invite.data?.email_sent === false) {
      const reason = await inviteFailureReason(invite.error || { message: invite.data?.error });
      setError(`The organization was created, but ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`);
    } else {
      setNotice(`Organization created. An invitation was sent to ${adminEmail}. Open the newest email on this computer.`);
    }
    load();
  }

  async function sendInvite(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    const email = inviteForm.email;
    const result = await supabase.functions.invoke("platform-invite-admin", {
      body: { organization_id: inviteFor.id, email, full_name: inviteForm.name, redirect_to: tenantLoginUrl() },
    });
    setBusy(false);
    if (result.error || result.data?.email_sent === false) {
      setError(await inviteFailureReason(result.error || { message: result.data?.error }));
      return;
    }
    setInviteFor(null);
    setInviteForm({ email: "", name: "" });
    setNotice(`An invitation was sent to ${email}. Open the newest email on this computer.`);
  }

  async function setStatus(id, status) {
    setStatusId(id);
    setError("");
    const { error: rpcError } = await supabase.rpc("platform_set_organization_status", { p_org: id, p_status: status });
    setStatusId("");
    if (rpcError) setError(rpcError.message);
    load();
  }

  const list = rows || [];
  const shown = list.filter((row) => `${row.name} ${row.slug} ${row.status}`.toLowerCase().includes(query.toLowerCase()));
  const emptyState = listError
    ? { icon: "clients", title: "Couldn't load clients", body: "Refresh the page to try again.", action: <EmptyAction onClick={load}>Try again</EmptyAction> }
    : query && list.length
    ? { icon: "search", title: "No matching clients", body: "Nothing matches that search. Try a different name, slug, or status.", action: <EmptyAction onClick={() => setQuery("")}>Clear search</EmptyAction> }
    : { icon: "clients", title: "No clients yet", body: "Create the first organization and invite its administrator.", action: <EmptyAction onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add client</EmptyAction> };
  const countLabel = rows === null ? "Loading clients" : `${list.length} ${list.length === 1 ? "client" : "clients"}`;

  return (
    <div>
      <PageHeader title="Clients" description="Create a customer organization and invite its first administrator. Visitor records stay inside that tenant.">
        <button type="button" className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d]" onClick={() => setOpen(true)}>
          <Icon name="plus" className="h-4 w-4" /> Add client
        </button>
      </PageHeader>
      {error && !open && !inviteFor ? <div className="mb-4"><Notice>{error}</Notice></div> : null}
      {notice ? <div className="mb-4"><Notice tone="info">{notice}</Notice></div> : null}

      <SlideForm open={open} title="Add client" description="The organization is created active, and the first administrator receives an email invitation." onClose={() => setOpen(false)}>
        <form onSubmit={createClient} className="space-y-4">
          {error ? <Notice>{error}</Notice> : null}
          <Field label="Client name">
            <input className={inputClass} required value={form.name} onChange={(event) => updateName(event.target.value)} />
          </Field>
          <Field label="Slug" hint="URL key">
            <input className={inputClass} required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" title="Lowercase letters, numbers, and single hyphens" value={form.slug} onChange={(event) => { setSlugEdited(true); setForm({ ...form, slug: event.target.value }); }} />
          </Field>
          <Field label="Support email" hint="Optional">
            <input className={inputClass} type="email" value={form.support_email} onChange={(event) => setForm({ ...form, support_email: event.target.value })} />
          </Field>
          <Field label="Administrator email">
            <input className={inputClass} required type="email" autoComplete="off" value={form.admin_email} onChange={(event) => setForm({ ...form, admin_email: event.target.value })} />
          </Field>
          <Field label="Administrator name" hint="Optional">
            <input className={inputClass} value={form.admin_name} onChange={(event) => setForm({ ...form, admin_name: event.target.value })} />
          </Field>
          <div className="flex gap-2 pt-2">
            <button className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white transition duration-200 hover:bg-[#c4631d] disabled:cursor-not-allowed disabled:opacity-50" disabled={busy}>
              <Icon name="plus" className="h-4 w-4" />
              {busy ? "Creating" : "Create client"}
            </button>
            <button type="button" className="h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      </SlideForm>
      <SlideForm open={Boolean(inviteFor)} title="Invite administrator" description={inviteFor ? `Send the first administrator invite for ${inviteFor.name}.` : ""} onClose={() => { setInviteFor(null); setError(""); }}>
        <form onSubmit={sendInvite} className="space-y-4">
          {error ? <Notice>{error}</Notice> : null}
          <Field label="Email">
            <input className={inputClass} required type="email" autoComplete="off" value={inviteForm.email} onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })} />
          </Field>
          <Field label="Name" hint="Optional">
            <input className={inputClass} value={inviteForm.name} onChange={(event) => setInviteForm({ ...inviteForm, name: event.target.value })} />
          </Field>
          <div className="flex gap-2 pt-2">
            <button className="inline-flex h-11 flex-1 items-center justify-center rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d] disabled:opacity-50" disabled={busy}>{busy ? "Sending" : "Send invite"}</button>
            <button type="button" className="h-11 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50" onClick={() => setInviteFor(null)}>Cancel</button>
          </div>
        </form>
      </SlideForm>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-brand-ink">All clients</h2>
            <p className="mt-0.5 text-xs text-slate-500">{countLabel}</p>
          </div>
          <label className="relative block w-full sm:w-64">
            <span className="sr-only">Search clients</span>
            <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:border-brand-blue focus:bg-white" placeholder="Search name, slug, or status" value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
        </div>
        <DataTable
          embedded
          loading={rows === null}
          empty={emptyState}
          rows={shown}
          columns={[
            { key: "name", label: "Client", render: (row) => (
              <span>
                <span className="block font-semibold text-brand-ink">{row.name}</span>
                <span className="text-xs text-slate-500">{row.slug}</span>
              </span>
            ) },
            { key: "status", label: "Status", render: (row) => <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${statusTone[row.status] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{row.status}</span> },
            { key: "plan", label: "Plan", render: (row) => row.plan || "No plan" },
            { key: "site_count", label: "Sites" },
            { key: "kiosk_count", label: "Kiosks" },
            { key: "actions", label: "Actions", render: (row) => (
              <span className="flex flex-wrap gap-2">
                <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-brand-blue hover:bg-sky-50" onClick={() => { setError(""); setInviteForm({ email: "", name: "" }); setInviteFor(row); }}>Invite admin</button>
                {row.status === "active" ? null : <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-brand-blue hover:bg-sky-50 disabled:opacity-50" disabled={statusId === row.id} onClick={() => setStatus(row.id, "active")}>Activate</button>}
                {row.status === "suspended" ? null : <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" disabled={statusId === row.id} onClick={() => setStatus(row.id, "suspended")}>Suspend</button>}
              </span>
            ) },
          ]}
        />
      </section>
    </div>
  );
}
